/**
 * Jovii — PLAYER, display formatting.
 *
 * Pure functions only: no DOM, no engine, no state. Everything the player screen
 * shows a human passes through here, so the phrasing is consistent and so the
 * awkward cases (unknown duration, a 0-byte size, an "und" language) have exactly
 * one answer each.
 */

import type { Candidate, MediaTrack, ProbeFailure, ProbedSource } from '$types';
import { normalizeTrackLanguage, resolveTrackLanguage } from '../track-language';

/* ────────────────────────────────────────────────────────────────────────────
 * Time
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * `1:04:09` / `4:09`. Hours only when there are hours, because the scrub bar's
 * two labels are the widest thing on a phone.
 */
export function timecode(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const whole = Math.floor(sec);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/** Time remaining, already negated: `-1:04:09`. Empty when the duration is unknown. */
export function remaining(positionSec: number, durationSec: number): string {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return '';
  return `-${timecode(Math.max(0, durationSec - positionSec))}`;
}

/** `2h 16m` / `47m`. For the title line, where a timecode reads like a clock. */
export function runtime(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '';
  const mins = Math.round(sec / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Sizes and rates
 * ──────────────────────────────────────────────────────────────────────────── */

export function bytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(n >= 10 * 1024 ** 3 ? 0 : 1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.round(n / 1024)} KB`;
}

export function bitrate(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return '';
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(1)} Mbps`;
  return `${Math.round(bps / 1e3)} kbps`;
}

/** Milliseconds, rounded the way a human reads them. */
export function millis(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  if (Math.abs(ms) >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${Math.round(ms)} ms`;
}

/** Drift, signed, in the units the eye cares about. */
export function drift(sec: number): string {
  const ms = sec * 1000;
  if (Math.abs(ms) < 1) return '0 ms';
  const sign = ms > 0 ? '+' : '−';
  return `${sign}${millis(Math.abs(ms))}`;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tracks
 * ──────────────────────────────────────────────────────────────────────────── */

const LANGUAGES: Record<string, string> = {
  eng: 'English', en: 'English',
  fra: 'French', fre: 'French', fr: 'French',
  deu: 'German', ger: 'German', de: 'German',
  spa: 'Spanish', es: 'Spanish',
  ita: 'Italian', it: 'Italian',
  nld: 'Dutch', dut: 'Dutch', nl: 'Dutch',
  por: 'Portuguese', pt: 'Portuguese',
  rus: 'Russian', ru: 'Russian',
  jpn: 'Japanese', ja: 'Japanese',
  kor: 'Korean', ko: 'Korean',
  zho: 'Chinese', chi: 'Chinese', zh: 'Chinese',
  ara: 'Arabic', ar: 'Arabic',
  hin: 'Hindi', hi: 'Hindi',
  swa: 'Swahili', sw: 'Swahili',
  pol: 'Polish', pl: 'Polish',
  swe: 'Swedish', sv: 'Swedish',
  nor: 'Norwegian', no: 'Norwegian', nob: 'Norwegian',
  dan: 'Danish', da: 'Danish',
  fin: 'Finnish', fi: 'Finnish',
  tur: 'Turkish', tr: 'Turkish',
  ell: 'Greek', gre: 'Greek', el: 'Greek',
  heb: 'Hebrew', he: 'Hebrew',
  ces: 'Czech', cze: 'Czech', cs: 'Czech',
  hun: 'Hungarian', hu: 'Hungarian',
  ron: 'Romanian', rum: 'Romanian', ro: 'Romanian',
  tha: 'Thai', th: 'Thai',
  vie: 'Vietnamese', vi: 'Vietnamese',
  ind: 'Indonesian', id: 'Indonesian',
  ukr: 'Ukrainian', uk: 'Ukrainian',
  bul: 'Bulgarian', hrv: 'Croatian', srp: 'Serbian', slk: 'Slovak', slv: 'Slovenian',
  est: 'Estonian', lav: 'Latvian', lit: 'Lithuanian', isl: 'Icelandic', fil: 'Filipino',
  msa: 'Malay', may: 'Malay', ben: 'Bengali', tam: 'Tamil', tel: 'Telugu', mal: 'Malayalam',
  und: 'Unknown'
};

/** "eng" → "English". Falls back to Intl, then to the tag itself. */
export function language(tag: string | undefined): string {
  if (!tag) return 'Unknown';
  const key = normalizeTrackLanguage(tag);
  const short = key.split('-')[0] ?? key;
  const hit = LANGUAGES[key] ?? LANGUAGES[short];
  if (hit) return hit;
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'language' });
    const name = dn.of(short);
    if (name && name !== short) return name;
  } catch {
    /* Intl.DisplayNames is missing on some old WebViews */
  }
  return tag.toUpperCase();
}

const CHANNEL_NAMES: Record<number, string> = { 1: 'Mono', 2: 'Stereo', 6: '5.1', 7: '6.1', 8: '7.1' };

export function channels(n: number | undefined): string {
  if (!n || n <= 0) return '';
  return CHANNEL_NAMES[n] ?? `${n}ch`;
}

const CODEC_NAMES: Record<string, string> = {
  h264: 'H.264', avc: 'H.264', hevc: 'HEVC', h265: 'HEVC', av1: 'AV1', vp9: 'VP9',
  aac: 'AAC', ac3: 'Dolby Digital', eac3: 'Dolby Digital+', dts: 'DTS', dca: 'DTS',
  truehd: 'TrueHD', mlp: 'TrueHD', flac: 'FLAC', opus: 'Opus', vorbis: 'Vorbis',
  mp3: 'MP3', mp2: 'MP2', alac: 'ALAC', pcm: 'PCM',
  subrip: 'SRT', ass: 'ASS', ssa: 'SSA', webvtt: 'VTT', hdmv_pgs_subtitle: 'PGS', dvd_subtitle: 'VobSub'
};

export function codecName(codec: string): string {
  const key = codec.toLowerCase();
  return CODEC_NAMES[key] ?? (key.startsWith('pcm') ? 'PCM' : codec.toUpperCase());
}

/**
 * "1080p" from the real frame size.
 *
 * Height alone lies about films: a 2.40:1 scope transfer is 1920×796, and calling
 * that "796p" helps nobody. Width is what the release name means.
 */
export function resolutionLabel(widthPx: number | undefined, heightPx: number | undefined): string {
  const w = widthPx ?? 0;
  const h = heightPx ?? 0;
  if (w >= 3400 || h >= 2000) return '4K';
  if (w >= 1800 || h >= 1000) return '1080p';
  if (w >= 1200 || h >= 700) return '720p';
  if (w >= 640 || h >= 400) return '480p';
  return h > 0 ? `${h}p` : '';
}

/**
 * The label a human picks an audio track by: language first, because that is the
 * decision; the technical part second, because that is the tie-break.
 */
export function audioTrackLabel(t: MediaTrack): string {
  const parts = [language(resolveTrackLanguage({ language: t.lang, title: t.title }))];
  if (t.title && t.title.toLowerCase() !== parts[0]!.toLowerCase()) parts.push(t.title);
  return parts.join(' · ');
}

export function audioTrackDetail(t: MediaTrack): string {
  const parts = [codecName(t.codec)];
  const ch = channels(t.channels);
  if (ch) parts.push(ch);
  // The one piece of engine truth worth surfacing: copied through, or decoded and
  // re-encoded to AAC in this browser. It explains a slower start.
  parts.push(t.passthrough ? 'direct' : 'converted');
  return parts.join(' · ');
}

export function subtitleTrackLabel(t: MediaTrack): string {
  const parts = [language(resolveTrackLanguage({ language: t.lang, title: t.title }))];
  if (t.title) parts.push(t.title);
  if (t.isForced) parts.push('forced');
  return parts.join(' · ');
}

/* ────────────────────────────────────────────────────────────────────────────
 * Candidates
 * ──────────────────────────────────────────────────────────────────────────── */

const PROVIDER_NAMES: Record<string, string> = {
  torrentio: 'Torrentio',
  mediafusion: 'MediaFusion',
  other: 'Other'
};

export function providerName(p: Candidate['provider']): string {
  return PROVIDER_NAMES[p] ?? p;
}

/** The picker's second line: provider · quality · size · bitrate. */
export function candidateDetail(c: Candidate): string {
  const parts: string[] = [providerName(c.provider)];
  if (c.quality !== 'unknown') parts.push(c.quality);
  const size = bytes(c.sizeBytes);
  if (size) parts.push(size);
  const rate = bitrate(c.bitrateBps);
  if (rate) parts.push(rate);
  if (c.audioHints.length) parts.push(c.audioHints.slice(0, 2).join('/'));
  return parts.join(' · ');
}

/** A short name for the "playing from" chip. Release names run to 90 characters. */
export function candidateShortName(c: Candidate): string {
  const q = c.quality !== 'unknown' ? ` ${c.quality}` : '';
  return `${providerName(c.provider)}${q}`;
}

const FAILURE_TEXT: Record<ProbeFailure, string> = {
  'http-error': 'the link is dead',
  'no-range-support': 'the server will not serve byte ranges',
  placeholder: 'a placeholder file, not the movie',
  'not-media': 'not a video file',
  'no-video-track': 'no video inside',
  'unsupported-video-codec': 'this device cannot decode that video',
  timeout: 'too slow to answer',
  aborted: 'cancelled'
};

/** Why a candidate was rejected, in words rather than codes. */
export function failureText(f: ProbeFailure | string | undefined): string {
  if (!f) return 'did not verify';
  return FAILURE_TEXT[f as ProbeFailure] ?? f;
}

/** What a verified probe says about the file, in one line. */
export function probeSummary(p: ProbedSource): string {
  const video = p.tracks.find((t) => t.kind === 'video');
  const audio = p.tracks.filter((t) => t.kind === 'audio');
  const subs = p.tracks.filter((t) => t.kind === 'subtitle');
  const parts: string[] = [];
  if (video) {
    const res = resolutionLabel(video.widthPx, video.heightPx);
    parts.push([codecName(video.codec), res].filter(Boolean).join(' '));
  }
  if (audio.length) parts.push(`${audio.length} audio`);
  if (subs.length) parts.push(`${subs.length} subs`);
  if (p.durationSec > 0) parts.push(runtime(p.durationSec));
  return parts.join(' · ');
}

/* ────────────────────────────────────────────────────────────────────────────
 * Subtitle cue text
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Cue text comes out of a stranger's MKV, so it is escaped first and only then are
 * the three tags WebVTT actually renders put back. Nothing else survives — a cue
 * carrying `<img onerror=…>` renders as literal text.
 */
export function cueHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped
    .replace(/&lt;(\/?)([biu])&gt;/g, '<$1$2>')
    .replace(/\r?\n/g, '<br>');
}
